import React, { useState } from "react";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCol,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonSpinner,
} from "@ionic/react";
import classNames from "classnames/bind";
import styles from "./BonusPoints.module.scss";
import { useCartData } from "../../../providers/cartDataStore";
import useGetReferralPointsQuery from "../../../providers/referrals/useGetReferralPointsQuery";
import { useConfig } from "../../../providers/config";
import { useAuth } from "../../../contexts/auth-context";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

const BonusPoints: React.FC = () => {
  const { t } = useTranslation();
  const { addRewardPoints, rewardPoints, cartTotalValue, resetRewardPoints } = useCartData();

  const config = useConfig();

  const { user } = useAuth();
  const userId = user?.id;

  const isGetReferralPointsQueryEnabled = config.isReferralEnabled && !!userId;

  const { data: userRewardPoints, isLoading } = useGetReferralPointsQuery({
    userId: userId!,
    options: {
      enabled: isGetReferralPointsQueryEnabled,
    },
  });

  const [pointsToUse, setPointsToUse] = useState("");
  const [pointsErrorMessage, setPointsErrorMessage] = useState("");

  const handleUseRewardPoints = () => {
    const points = parseInt(pointsToUse, 10);
    if (isNaN(points) || points < 0 || points > cartTotalValue) {
      setPointsErrorMessage(t("Incorrect input"));
      return;
    } else {
      setPointsErrorMessage("");
    }

    if (points + rewardPoints > (userRewardPoints ?? 0)) {
      setPointsErrorMessage(t("Not enough bonus points"));
      return;
    } else {
      setPointsErrorMessage("");
    }

    addRewardPoints(points);
  };

  return (
    <IonCard>
      <IonCardContent>
        <IonRow className={cx("ion-margin-top ion-margin-start", "ion-align-items-center")}>
          <IonCol className={cx("ion-no-padding")} size="7">
            {`${t("Your bonus points")}:`}
          </IonCol>
          <IonCol size="5" className={cx("ion-text-end")}>
            {isLoading ? <IonSpinner name="dots" /> : userRewardPoints}
          </IonCol>
        </IonRow>
        <IonRow className={cx("ion-margin-top ion-margin-start", "ion-align-items-center")}>
          <IonCol className={cx("ion-no-padding")} size="7">
            {`${t("Applied bonus points")}:`}
          </IonCol>
          <IonCol size="5" className={cx("ion-text-end")}>
            {rewardPoints}
          </IonCol>
        </IonRow>
        {rewardPoints > 0 ? (
          <IonRow>
            <IonCol className={cx("ion-no-padding")}>
              <IonButton color="primary" expand="block" onClick={resetRewardPoints}>
                {t("Reset bonus points")}
              </IonButton>
            </IonCol>
          </IonRow>
        ) : (
          <IonRow>
            <IonCol className={cx("ion-no-padding")}>
              <IonItem className={cx("ion-no-padding ion-margin-start")}>
                <IonInput
                  type="number"
                  min="0"
                  max={String(userRewardPoints)}
                  pattern="[0-9]*"
                  placeholder={t("Use points")}
                  onIonChange={(e) => setPointsToUse(e.detail.value ?? "")}
                />
              </IonItem>
              {pointsErrorMessage && (
                <IonItem lines="none" className={cx("ion-no-padding ion-margin-start")}>
                  <IonLabel className="ion-text-wrap" color="danger">
                    {pointsErrorMessage}
                  </IonLabel>
                </IonItem>
              )}
              <IonButton color="primary" expand="block" onClick={handleUseRewardPoints}>
                {t("Apply bonus points")}
              </IonButton>
            </IonCol>
          </IonRow>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export { BonusPoints };
